import React, {useEffect, useRef, useState} from 'react';
import RichTextEditor from './editor/RichTextEditor';
import SourceEditor from './editor/SourceEditor';

const formatForMode = mode => mode === 'markdown' ? 'markdown' : 'html';
const modeLabel = mode => mode === 'richText' ? 'Rich text' : mode === 'markdown' ? 'Markdown' : 'HTML';
const MODES = ['richText', 'markdown', 'html'];

export default function ContentEditorFeature({
    content,
    mode,
    format,
    initialContent = '',
    initialMode = 'richText',
    onChange,
    onModeChange,
    onConversionRequired,
    onPasteImage,
    onInsertImage,
}) {
    const controlled = typeof content === 'string';
    const [localContent, setLocalContent] = useState(initialContent);
    const [localMode, setLocalMode] = useState(initialMode);
    const tabRefs = useRef([]);
    const activeContent = controlled ? content : localContent;
    const activeMode = mode || localMode;
    const activeFormat = format || formatForMode(activeMode);

    useEffect(() => {
        if (!controlled) setLocalContent(initialContent);
    }, [controlled, initialContent]);

    const updateContent = value => {
        if (!controlled) setLocalContent(value);
        onChange?.(value, activeMode);
    };

    const requestMode = nextMode => {
        if (nextMode === activeMode) return;
        const nextFormat = formatForMode(nextMode);
        if (nextFormat !== activeFormat) {
            onConversionRequired?.({mode: nextMode, format: nextFormat});
            return;
        }
        if (!mode) setLocalMode(nextMode);
        onModeChange?.(nextMode);
    };

    const handleTabKey = (event, index) => {
        let nextIndex = null;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % MODES.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + MODES.length) % MODES.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = MODES.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        tabRefs.current[nextIndex]?.focus();
        requestMode(MODES[nextIndex]);
    };

    return <section className="content-editor-feature" aria-label="Content editor">
        <div role="tablist" aria-label="Authoring mode" className="content-mode-tabs">
            {MODES.map((option, index) => <button
                aria-selected={activeMode === option}
                aria-controls={`content-mode-panel-${option}`}
                className="content-mode-tab"
                id={`content-mode-${option}`}
                key={option}
                onClick={() => requestMode(option)}
                onKeyDown={event => handleTabKey(event, index)}
                ref={element => { tabRefs.current[index] = element; }}
                role="tab"
                tabIndex={activeMode === option ? 0 : -1}
                type="button"
            >{modeLabel(option)}</button>)}
        </div>
        <div role="tabpanel" id={`content-mode-panel-${activeMode}`}
            aria-labelledby={`content-mode-${activeMode}`}>
            {activeMode === 'richText'
                ? <RichTextEditor
                    value={activeContent}
                    onChange={updateContent}
                    onPasteImage={onPasteImage}
                    onInsertImage={onInsertImage}
                />
                : <>
                    {onInsertImage && <div className="content-editor-toolbar" role="toolbar" aria-label="Source tools">
                        <button type="button" className="content-editor-tool-button" onClick={onInsertImage}>
                            Insert image
                        </button>
                    </div>}
                    <SourceEditor value={activeContent} mode={activeMode} onChange={updateContent} />
                </>}
        </div>
    </section>;
}
